import { useCallback, useEffect, useState } from 'react';

import { LocalStorageKey } from 'lib/clients/StorageClient';
import useLocalStorage from 'lib/hooks/useLocalStorage';

// user session's access token expires in 12 hours
const LOG_EXPIRATION_SECONDS = 12 * 60 * 60;

type DashboardEventPublishModule = {
  name: 'publish-module';
  payload: string;
};

type DashboardEventOther = {
  name: 'other';
  payload: number;
};

type DashboardEvent = (DashboardEventPublishModule | DashboardEventOther) & { timestamp: number };
type EventPayloadMap = { [E in DashboardEvent as E['name']]: E['payload'] };

// This hook is designed for passing events between multiple tabs/windows
// If your usage is just within the same screen, think using Redux or component props
export default <K extends keyof EventPayloadMap>(
  name: K,
  listener?: (payload: EventPayloadMap[K]) => void,
) => {
  const [lastlyCheckedEventTimestamp, setLastlyCheckedEventTimestamp] = useState(Date.now());
  const [logs, setLogs] = useLocalStorage<DashboardEvent[]>(LocalStorageKey.DashboardEventBus, [], {
    sync: true,
  });

  useEffect(() => {
    const eventsWaitForNotify = logs.filter(
      ({ timestamp }) => timestamp > lastlyCheckedEventTimestamp,
    );
    if (eventsWaitForNotify.length > 0) {
      setLastlyCheckedEventTimestamp(Date.now());
      eventsWaitForNotify.forEach((event) => {
        if (event.name === name) {
          listener?.(event.payload as EventPayloadMap[typeof name]);
        }
      });
    }
  }, [logs, lastlyCheckedEventTimestamp, name, listener]);

  const triggerEvent = useCallback(
    (payload: EventPayloadMap[K]) => {
      setLogs((currentLogs) => {
        const now = Date.now();
        const youngerLogs = currentLogs.filter(
          ({ timestamp }) => timestamp > now - LOG_EXPIRATION_SECONDS,
        );
        return [...youngerLogs, { name, payload, timestamp: now } as DashboardEvent];
      });
    },
    [name, setLogs],
  );

  return triggerEvent;
};
